'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Poppins } from 'next/font/google';
import { BRAND_BLUE, BRAND_GREEN, BRAND_YELLOW } from '@voley/shared-types/constants';

const poppins = Poppins({ subsets: ['latin'], weight: ['700'] });

export default function ContactPage() {
  // Brand color to align with landing/pricing
  const primaryColor = BRAND_BLUE;
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    reason: 'general'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Clear previous errors
    setErrors({});
    
    // Client-side validation
    const newErrors: {[key: string]: string} = {};
    
    if (formData.message.trim().length < 10) {
      newErrors.message = 'Please provide a message with at least 10 characters.';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Send to external requests API
      const response = await fetch('/api/public/external-requests/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit contact request');
      }

      await response.json();
      
      setSubmitted(true);
      
      // Reset form after submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        reason: 'general'
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (error) {
      setErrors({ form: error instanceof Error ? error.message : 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="bg-white py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1
              className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl ${poppins.className} tracking-tight leading-tight`}
            >
              <span className="block font-medium text-gray-900">
                Talk with the team
              </span>
              <span className="block font-medium text-gray-900">
                behind Voley.
              </span>
            </h1>
            <p className="mt-4 sm:mt-6 max-w-4xl mx-auto text-base sm:text-lg md:text-xl lg:text-2xl font-light text-gray-600 px-2">
              Reach out for sales, support, or general questions—we&apos;ll help you map Voley to your
              agency&apos;s workflows.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: primaryColor }}
              />
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: BRAND_GREEN }}
              />
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: BRAND_YELLOW }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form and Info Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                Contact
              </h2>
              
              <div className="space-y-8">
                {/* Headquarters */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Headquarters</h3>
                  <p className="text-gray-600">
                    1555 Freedom Blvd 200 W<br />
                    Provo, UT 84604
                  </p>
                </div>
                
                {/* Support */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Support</h3>
                  <p className="text-gray-600 mb-1">
                    <span className="font-medium">Email:</span> support@voley.us
                  </p>
                  <p className="text-gray-600 mb-1">
                    <span className="font-medium">Phone:</span> (801) 854-8750
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Hours:</span> Mon-Fri, 8am-6pm MT
                  </p>
                </div>
                
                {/* Sales */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Sales</h3>
                  <p className="text-gray-600 mb-1">
                    <span className="font-medium">Email:</span> jared@voley.us
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Phone:</span> (530) 826-6866
                  </p>
                </div>
                
                {/* Social Media */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Connect with us</h3>
                  <div className="flex space-x-4">
                    <a href="https://www.linkedin.com/company/letsvoley/posts/?feedView=all" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-brand transition-colors">
                      <span className="sr-only">LinkedIn</span>
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Form with Gradient Background */}
            <div className="lg:col-span-2">
              <div 
                className="rounded-3xl p-2"
                style={{ background: 'linear-gradient(135deg, rgba(53, 129, 207, 0.3) 0%, rgba(134, 208, 157, 0.3) 50%, rgba(243, 221, 96, 0.3) 100%)' }}
              >
                <div className="bg-white rounded-2xl p-6 sm:p-8">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Send us a message</h2>
                  
                  {submitted ? (
                    <div className="bg-brand-green/15 border border-brand-green/30 rounded-xl p-4 mb-6">
                      <div className="flex">
                        <div className="shrink-0">
                          <svg className="h-5 w-5 text-[#5aaa7a]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-[#3d7a5a]">Message sent successfully</h3>
                          <div className="mt-1 text-sm text-[#4a9a6a]">
                            <p>Thank you for contacting us. We&apos;ll get back to you as soon as possible.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                          First name
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleChange}
                          className="shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-lg p-2.5 border"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                          Last name
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleChange}
                          className="shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-lg p-2.5 border"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Work email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-lg p-2.5 border"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                          Phone number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="555-123-4567"
                          className="shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-lg p-2.5 border"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                        Agency / Company name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-lg p-2.5 border"
                      />
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                        How can we help you?
                      </label>
                      <select
                        id="reason"
                        name="reason"
                        required
                        value={formData.reason}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-lg p-2.5 border"
                      >
                        <option value="general">General inquiry</option>
                        <option value="sales">Sales information</option>
                        <option value="support">Technical support</option>
                        <option value="partnership">Partnership opportunity</option>
                        <option value="careers">Careers</option>
                      </select>
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        required
                        value={formData.message}
                        onChange={handleChange}
                        className={`shadow-sm block w-full sm:text-sm rounded-lg p-2.5 border ${
                          errors.message
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-brand focus:ring-brand'
                        }`}
                        placeholder="Please describe how we can help you (minimum 10 characters)..."
                      />
                      {errors.message ? (
                        <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                      ) : (
                        <p className="mt-1 text-sm text-gray-500">
                          {formData.message.length}/10 minimum characters
                        </p>
                      )}
                    </div>
                    
                    {errors.form && (
                      <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3">
                        <p className="text-sm text-red-700">{errors.form}</p>
                      </div>
                    )}

                    <div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full inline-flex justify-center items-center px-4 py-3 border-0 text-base font-medium rounded-full shadow-sm text-white hover:shadow-lg transition-all hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {isSubmitting ? 'Sending...' : 'Send message'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">
              Commonly asked questions
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-gray-600 lg:mx-auto">
              Find quick answers to frequently asked questions
            </p>
          </div>
          
          <div className="mt-12 max-w-3xl mx-auto space-y-4">
            <div className="bg-white border border-gray-200 overflow-hidden rounded-2xl hover:shadow-md transition-shadow">
              <div className="px-6 py-5">
                <h3 className="text-lg font-semibold text-gray-900">What are your support hours?</h3>
                <p className="mt-2 text-gray-600">
                  We&apos;re available Monday through Friday from 8am to 6pm Mountain Time. For urgent issues, please email us and we&apos;ll get back to you as soon as possible.
                </p>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 overflow-hidden rounded-2xl hover:shadow-md transition-shadow">
              <div className="px-6 py-5">
                <h3 className="text-lg font-semibold text-gray-900">How quickly can I expect a response?</h3>
                <p className="mt-2 text-gray-600">
                  For general inquiries, we aim to respond within 1 business day. Support requests are typically addressed within 4 hours during business hours.
                </p>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 overflow-hidden rounded-2xl hover:shadow-md transition-shadow">
              <div className="px-6 py-5">
                <h3 className="text-lg font-semibold text-gray-900">Do you offer training?</h3>
                <p className="mt-2 text-gray-600">
                  Yes, we offer personalized training via video calls. We&apos;ll guide you through the platform features and help customize it to your agency&apos;s specific needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-brand/75 via-brand-green/75 to-brand-yellow/75">
        <div className="max-w-7xl mx-auto py-10 sm:py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white">
            <span className="block">Ready to see Voley in action?</span>
            <span className="block opacity-90 font-medium">Schedule a personalized demo today.</span>
          </h2>
          <div className="mt-6 sm:mt-8 flex lg:mt-0 lg:shrink-0">
            <div className="inline-flex w-full sm:w-auto">
              <Link
                href="/request-demo"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-gray-900 bg-white hover:bg-slate-50 transition-all hover:scale-[1.02] hover:shadow-lg w-full sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
